import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';

import { getPageTitle, getPageDescription, getSiteVerificationId } from '/lib/helpers';
import { replaceString } from '/imports/core/ui/helpers';
import useDomain from '/imports/core/hooks/useDomain';
import useIntl from '/imports/core/api/useIntl';
import { useRouter } from 'next/router';
import { BLUEPILL_HOSTS } from 'imports/core/api/constants';
import { useResponsive } from 'imports/core/api/responsiveContext';

const Meta = ({ domain }) => {
  const router = useRouter();
  const isCancelPage = router.asPath.includes('cancel-subscription');
  const { locale, t } = useIntl();
  const { getBrandName } = useDomain();
  const brandName = getBrandName(true, domain);
  const title = isCancelPage
    ? replaceString(t('meta_title_builder'), { '{BRAND}': brandName })
    : replaceString(t(getPageTitle(domain)), { Resumedone: brandName });
  const description = isCancelPage
    ? replaceString(t('meta_description_builder'), { '{BRAND}': brandName })
    : replaceString(t(getPageDescription(domain)), { Resumedone: brandName });
  const [content, setContent] = useState(description);
  const [contentTitle, setContentTitle] = useState(title);
  const verificationId = getSiteVerificationId(domain);
  const { host } = useResponsive();
  const isAutoApply = BLUEPILL_HOSTS.includes(host);
  const optionsTitle = t('bluepill_resume_option_meta_title');
  const optionsDesc = t('bluepill_resume_option_meta_description');
  const isResumeOtion = isAutoApply && router.asPath.includes('resume-options');
  useEffect(() => {
    document.querySelector('meta[name="description"]').setAttribute('lang', locale);
    const updatedDescription = isCancelPage
      ? replaceString(t('meta_description_builder'), {
          '{BRAND}': brandName,
        })
      : replaceString(t(getPageDescription(domain)), {
          Resumedone: brandName,
        });
    const updatedTitle = isCancelPage
      ? replaceString(t('meta_title_builder'), {
          '{BRAND}': brandName,
        })
      : replaceString(t(getPageTitle(domain)), {
          Resumedone: brandName,
        });
    setContent(updatedDescription);
    setContentTitle(updatedTitle);
  }, [locale]);

  return (
    <Head>
      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no, user-scalable = no" />
      <meta name="google-site-verification" content={verificationId} />
      <title>{isResumeOtion ? optionsTitle : contentTitle || title}</title>
      <meta name="description" lang={locale} content={isResumeOtion ? optionsDesc : content || description} />
      <link rel="titleapple-touch-icon" sizes="120x120" href="/static/apple-touch-icon-precomposed.png" />
    </Head>
  );
};

Meta.propTypes = {
  domain: PropTypes.string,
};

Meta.displayName = 'Meta';

export default Meta;
